import SalaryPaymentModal from './SalaryPaymentModal';
import { Box, Grid, Chip, LinearProgress, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { PeopleOutlined, VerifiedUserOutlined, AssignmentTurnedInOutlined, WorkOutlined } from '@mui/icons-material';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ReTooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

// Hooks & Utils
import { useHrDashboard } from './hooks/useHrDashboard';
import { renderPieLabel, formatPercentage, fadeUp } from './utils/dashboardUtils';

// Modular Components
import KpiCard from './components/KpiCard';
import SectionCard from './components/SectionCard';
import DashboardHeader from './components/DashboardHeader';
import EmployeeTable from './components/EmployeeTable';
import EmployeeDetailsModal from './components/EmployeeDetailsModal';
import QuickViewModal from './components/PortalAssignModal';
import DeactivationModal from './components/DeactivationModal';
import SalaryListModal from './components/SalaryListModal';
import UpdateStatusModal from './components/UpdateStatusModal';

const HRLandingPage = () => {
    const {
        employees,
        activeRole,
        setActiveRole,
        salaryModalOpen,
        selectedSalaryEmployee,
        handleOpenSalaryModal,
        handleCloseSalaryModal,
        handleOpenEditSalaryModal,
        editSalaryData,
        handleSalarySubmit,
        stats,
        filteredEmployees,
        roleDistribution,
        stateHeadcount,
        monthlyJoins,
        bgvPieData,
        trainingPieData,
        detailsModalOpen,
        selectedDetailsEmployee,
        handleOpenDetailsModal,
        handleCloseDetailsModal,
        quickViewOpen,
        selectedQuickViewEmployee,
        handleOpenQuickView,
        handleCloseQuickView,
        page,
        rowsPerPage,
        totalCount,
        handleChangePage,
        handleChangeRowsPerPage,
        deactivationModalOpen,
        selectedDeactivationEmployee,
        handleOpenDeactivationModal,
        handleCloseDeactivationModal,
        salaryListModalOpen,
        selectedSalaryListEmployee,
        handleOpenSalaryListModal,
        handleCloseSalaryListModal,
        statusModalOpen,
        selectedStatusType,
        selectedStatusEmployee,
        handleOpenStatusModal,
        handleCloseStatusModal,
        refreshDashboard
    } = useHrDashboard();
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 6 }}>
            <DashboardHeader />

            <Box sx={{ px: { xs: 2, md: 4 }, mt: -5 }}>
                {/* ── KPI Cards ── */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {[
                        {
                            icon: PeopleOutlined,
                            label: 'Total Employees',
                            value: stats.total,
                            subtitle: 'All roles combined',
                            color: '#1e40af'
                        },
                        {
                            icon: WorkOutlined,
                            label: 'Active Members',
                            value: stats.active,
                            subtitle: 'Currently active',
                            color: '#14b8a6'
                        },
                        {
                            icon: VerifiedUserOutlined,
                            label: 'BGV Verified',
                            value: stats.bgvVerified,
                            subtitle: 'Background verified',
                            color: '#22c55e'
                        },
                        {
                            icon: AssignmentTurnedInOutlined,
                            label: 'Training Done',
                            value: stats.trained,
                            subtitle: 'Completed training',
                            color: '#8b5cf6'
                        }
                    ].map((kpi, i) => (
                        <Grid key={kpi.label} item xs={12} sm={6} lg={3}>
                            <KpiCard {...kpi} index={i} />
                        </Grid>
                    ))}
                </Grid>
                {/* ── Progress Indicators (Charts) ── */}
                <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} custom={1}>
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={7}>
                            <SectionCard title="Monthly Hiring Trend" subtitle="New employees added per month" index={0}>
                                <ResponsiveContainer width="100%" height={220}>
                                    <AreaChart data={monthlyJoins} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="hiringGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#1e40af" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#1e40af" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <ReTooltip
                                            contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="employees"
                                            stroke="#1e40af"
                                            strokeWidth={2.5}
                                            fill="url(#hiringGrad)"
                                            dot={{ r: 4, fill: '#1e40af' }}
                                            activeDot={{ r: 6 }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </SectionCard>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <SectionCard title="Role Distribution" subtitle="Employees by hierarchy level" index={1}>
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie
                                            data={roleDistribution}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={75}
                                            paddingAngle={3}
                                            dataKey="value"
                                            label={renderPieLabel}
                                        >
                                            {roleDistribution.map((entry) => (
                                                <Cell
                                                    key={entry.name}
                                                    fill={entry.color}
                                                    opacity={activeRole && activeRole !== entry.name ? 0.4 : 1}
                                                />
                                            ))}
                                        </Pie>
                                        <ReTooltip
                                            contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                        />
                                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                                {activeRole && (
                                    <Box sx={{ textAlign: 'center', mt: -1 }}>
                                        <Chip
                                            label={`Filtered: ${activeRole}`}
                                            size="small"
                                            color="primary"
                                            onDelete={() => setActiveRole(null)}
                                        />
                                    </Box>
                                )}
                            </SectionCard>
                        </Grid>
                    </Grid>
                </motion.div>

                {/* ── State & Status Graphs ── */}
                <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} custom={2}>
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={5}>
                            <SectionCard title="Headcount by State" subtitle="Employees per assigned state" index={2}>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={stateHeadcount} margin={{ top: 5, right: 5, left: -25, bottom: 0 }} barSize={28}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="state" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <ReTooltip
                                            contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                        />
                                        <Bar dataKey="count" name="Employees" radius={[6, 6, 0, 0]}>
                                            {stateHeadcount.map((_, i) => (
                                                <Cell key={i} fill={['#1e40af', '#14b8a6', '#8b5cf6', '#f59e0b'][i % 4]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </SectionCard>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3.5}>
                            <SectionCard title="BGV Status" subtitle="Background verification" index={3}>
                                <ResponsiveContainer width="100%" height={190}>
                                    <PieChart>
                                        <Pie
                                            data={bgvPieData}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={65}
                                            paddingAngle={4}
                                            dataKey="value"
                                            label={renderPieLabel}
                                        >
                                            {bgvPieData.map((e) => (
                                                <Cell key={e.name} fill={e.color} />
                                            ))}
                                        </Pie>
                                        <ReTooltip
                                            contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                        />
                                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <Box sx={{ px: 1, mt: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Verification Rate
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} color="success.main">
                                            {stats.total ? formatPercentage((stats.bgvVerified / stats.total) * 100) : '0%'}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={stats.total ? (stats.bgvVerified / stats.total) * 100 : 0}
                                        sx={{
                                            borderRadius: 4,
                                            height: 6,
                                            bgcolor: 'grey.100',
                                            '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 4 }
                                        }}
                                    />
                                </Box>
                            </SectionCard>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3.5}>
                            <SectionCard title="Training Status" subtitle="Employee training progress" index={4}>
                                <ResponsiveContainer width="100%" height={190}>
                                    <PieChart>
                                        <Pie
                                            data={trainingPieData}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={65}
                                            paddingAngle={4}
                                            dataKey="value"
                                            label={renderPieLabel}
                                        >
                                            {trainingPieData.map((e) => (
                                                <Cell key={e.name} fill={e.color} />
                                            ))}
                                        </Pie>
                                        <ReTooltip
                                            contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                                        />
                                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <Box sx={{ px: 1, mt: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Completion Rate
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} color="success.main">
                                            {stats.total ? formatPercentage((stats.trained / stats.total) * 100) : '0%'}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={stats.total ? (stats.trained / stats.total) * 100 : 0}
                                        sx={{
                                            borderRadius: 4,
                                            height: 6,
                                            bgcolor: 'grey.100',
                                            '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 4 }
                                        }}
                                    />
                                </Box>
                            </SectionCard>
                        </Grid>
                    </Grid>
                </motion.div>

                {/* ── Employee Table ── */}
                <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.05 }} custom={3}>
                    <EmployeeTable
                        employees={employees}
                        filteredEmployees={filteredEmployees}
                        activeRole={activeRole}
                        totalCount={totalCount}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        setActiveRole={setActiveRole}
                        onOpenSalaryModal={handleOpenSalaryModal}
                        onOpenDetailsModal={handleOpenDetailsModal}
                        onOpenQuickView={handleOpenQuickView}
                        onOpenDeactivationModal={handleOpenDeactivationModal}
                        onOpenSalaryListModal={handleOpenSalaryListModal}
                        onOpenStatusModal={handleOpenStatusModal}
                    />
                </motion.div>
            </Box>

            <EmployeeDetailsModal open={detailsModalOpen} handleClose={handleCloseDetailsModal} employee={selectedDetailsEmployee} />

            <SalaryPaymentModal
                open={salaryModalOpen}
                handleClose={handleCloseSalaryModal}
                employee={selectedSalaryEmployee}
                editData={editSalaryData}
                onSubmit={handleSalarySubmit}
            />

            <QuickViewModal
                open={quickViewOpen}
                handleClose={handleCloseQuickView}
                employee={selectedQuickViewEmployee}
                onRefresh={refreshDashboard}
            />

            <DeactivationModal
                open={deactivationModalOpen}
                handleClose={handleCloseDeactivationModal}
                employee={selectedDeactivationEmployee}
                onRefresh={refreshDashboard}
            />

            <SalaryListModal
                open={salaryListModalOpen}
                handleClose={handleCloseSalaryListModal}
                employee={selectedSalaryListEmployee}
                onOpenSalaryModal={handleOpenSalaryModal}
                onOpenEditSalaryModal={handleOpenEditSalaryModal}
                isSalaryModalOpen={salaryModalOpen}
                onRefresh={refreshDashboard}
            />

            <UpdateStatusModal
                open={statusModalOpen}
                handleClose={handleCloseStatusModal}
                employee={selectedStatusEmployee}
                type={selectedStatusType}
                onRefresh={refreshDashboard}
            />
        </Box>
    );
};

export default HRLandingPage;
